import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Download, CloudUpload } from "lucide-react";
import { exportUrl } from "@/lib/api";
import { getMeta, META } from "@/lib/db";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";
import { useFarm } from "@/lib/farmContext";
import { useLots, useAnimals } from "@/lib/repo";
import { toast } from "sonner";

const ALL = "__all__";

// Fix J (2026-09-17): el cliente necesita exportar una finca en particular;
// antes solo se podía bajar el rebaño completo. Los filtros son opcionales:
// con "Todas las fincas" / "Todos" la descarga es exactamente la de siempre.
const STATUS_OPTIONS = [
  { v: "active", l: "Activos" },
  { v: "sold", l: "Vendidos" },
  { v: "slaughtered", l: "Sacrificados" },
  { v: "dead", l: "Muertos" },
];

export default function ExportSettings() {
  const navigate = useNavigate();
  const online = useOnline();
  const { farms } = useFarm();
  const [token, setToken] = useState("");
  const [farmId, setFarmId] = useState(ALL);
  const [lotId, setLotId] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  useEffect(() => { getMeta(META.AUTH_TOKEN).then((t) => setToken(t || "")); }, []);

  const lots = useLots(farmId === ALL ? null : farmId);
  useEffect(() => { setLotId(ALL); }, [farmId]);

  const filters = useMemo(() => ({
    farmId: farmId === ALL ? undefined : farmId,
    lotId: lotId === ALL ? undefined : lotId,
    status: status === ALL ? undefined : status,
  }), [farmId, lotId, status]);

  // El conteo se calcula con el espejo local (IndexedDB): instantáneo y
  // funciona sin señal, como el resto de la app.
  const matching = useAnimals({
    farm_id: filters.farmId,
    lot_id: filters.lotId,
    status: filters.status,
  });
  const pendingInSelection = matching.filter((a) => a._sync_state && a._sync_state !== "synced").length;

  const dl = async (entity, format) => {
    // Para que el navegador pueda descargar con auth, usamos fetch + blob.
    // Los eventos aceptan el filtro de finca; los inventarios quedan como están.
    const entityFilters = entity === "animals" ? filters
      : entity === "events" || entity === "inventories" ? { farmId: filters.farmId } : {};
    try {
      const res = await fetch(exportUrl(entity, format, entityFilters), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(res.status === 403
          ? "No tenés acceso a esa finca"
          : "No se pudo generar la exportación");
        return;
      }
      // El servidor manda el nombre con la finca (animales-la-fundadora.xlsx).
      // Si el encabezado no llega, se usa el nombre de siempre.
      const cd = res.headers.get("Content-Disposition") || "";
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fallback = { animals: "animales", events: "eventos", inventories: "inventarios" }[entity] || entity;
      a.download = match ? decodeURIComponent(match[1]) : `${fallback}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo generar la exportación");
    }
  };

  const selectedFarmName = farms.find((f) => f.id === farmId)?.name;

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold">Exportar datos</h1>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Qué exportar</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Finca</Label>
              <Select value={farmId} onValueChange={setFarmId}>
                <SelectTrigger className="h-11" data-testid="export-farm-select">
                  <SelectValue placeholder="Todas las fincas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todas las fincas</SelectItem>
                  {farms.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Lote</Label>
              <Select value={lotId} onValueChange={setLotId} disabled={farmId === ALL}>
                <SelectTrigger className="h-11" data-testid="export-lot-select">
                  <SelectValue placeholder="Todos los lotes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos los lotes</SelectItem>
                  {lots.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-11" data-testid="export-status-select">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="text-xs text-muted-foreground" data-testid="export-count-preview">
            Se exportarán <span className="font-semibold text-foreground">{matching.length}</span> animales
            {selectedFarmName ? <> de <span className="font-medium text-foreground">{selectedFarmName}</span></> : " de todas las fincas"}.
          </p>
          {pendingInSelection > 0 && (
            <p className="text-xs flex items-start gap-1.5 text-[hsl(var(--brand-ochre))]" data-testid="export-pending-warning">
              <CloudUpload size={13} className="mt-0.5 shrink-0" />
              <span>
                {pendingInSelection} de estos registros todavía no se subieron al servidor y NO van a estar en el archivo.
                Sincronizá primero para incluirlos.
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Descargas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!online && <OfflineNotice label="La exportación de datos requiere conexión." />}
          {[
            { e: "animals", l: "Animales" },
            { e: "events", l: "Eventos / Historial" },
            { e: "inventories", l: "Inventarios" },
          ].map((row) => (
            <div key={row.e} className="flex items-center justify-between gap-2 border border-border rounded-xl p-3">
              <span className="text-sm font-medium">{row.l}</span>
              <div className="flex gap-2">
                {/* Fix G (2026-09-02): Excel primero y destacado — es el formato que usa
                    el cliente. CSV y JSON quedan disponibles para quien los necesite.
                    Fix L (2026-09-21): Inventarios ya tiene Excel: una fila por conteo,
                    legible, sin los arrays de UUID que devolvía antes. */}
                <Button size="sm" disabled={!online} onClick={()=>dl(row.e, "xlsx")} data-testid={`export-${row.e}-xlsx`}><Download size={14} className="mr-1"/>Excel</Button>
                <Button variant="outline" size="sm" disabled={!online} onClick={()=>dl(row.e, "csv")} data-testid={`export-${row.e}-csv`}><Download size={14} className="mr-1"/>CSV</Button>
                <Button variant="outline" size="sm" disabled={!online} onClick={()=>dl(row.e, "json")} data-testid={`export-${row.e}-json`}><Download size={14} className="mr-1"/>JSON</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
